import { useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import DateInput from "../components/DateInput";
import {
  MapTrifold, House, NavigationArrow, Clock, MapPin, Warning,
  PencilSimple, CarSimple, Path, Lightning, ListChecks, Wrench
} from "@phosphor-icons/react";

const TYPE_STYLE = {
  job: { icon: Wrench, label: "Job", cls: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200" },
  emergency: { icon: Lightning, label: "Emergency", cls: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200" },
  task: { icon: ListChecks, label: "Task", cls: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200" },
};

function TypeBadge({ type }) {
  const t = TYPE_STYLE[type] || TYPE_STYLE.job;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${t.cls}`}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

function HomeLocationCard({ home, onSaved }) {
  const [editing, setEditing] = useState(!home);
  const [query, setQuery] = useState(home?.address || "");
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setEditing(!home); setQuery(home?.address || ""); }, [home]);

  const save = async () => {
    const address = (picked?.formatted_address || query || "").trim();
    if (!address) { toast.error("Enter your home address"); return; }
    setSaving(true);
    try {
      const { data } = await API.put("/in-house-portal/home-location", {
        address,
        latitude: picked?.latitude ?? null,
        longitude: picked?.longitude ?? null,
      });
      toast.success("Home location saved");
      setEditing(false);
      setPicked(null);
      onSaved(data.home_location);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save home location");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid="home-location-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          <House className="h-4 w-4 text-emerald-600" weight="fill" /> Home location
        </div>
        {home && !editing && (
          <button onClick={() => setEditing(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  data-testid="edit-home-btn">
            <PencilSimple className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>
      {!editing ? (
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300" data-testid="home-address-text">
          <MapPin className="h-3.5 w-3.5 inline mr-1 text-slate-400" />{home?.address}
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <AddressAutocomplete
            value={query}
            onChange={(v) => { setQuery(v); setPicked(null); }}
            onSelect={(s) => { setPicked(s); setQuery(s.formatted_address || query); }}
            placeholder="Your home address or postcode…"
            data-testid="home-address-autocomplete"
          />
          <p className="text-[11px] text-slate-500">
            Your route each day starts and ends here. Pick a suggestion for pin-point accuracy — typed addresses are located automatically.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-home-btn">
              {saving ? "Saving…" : "Save home location"}
            </Button>
            {home && (
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setQuery(home.address); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StopToggleRow({ stop, fixed, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 px-3 border-t border-border first:border-t-0"
         data-testid={`day-stop-${stop.key}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge type={stop.type} />
          {stop.scheduled_time && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
              <Clock className="h-3 w-3" /> {stop.scheduled_time}
            </span>
          )}
          <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{stop.title}</span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-0.5">{stop.address}</p>
      </div>
      {stop.scheduled_time ? (
        <button onClick={() => onToggle(stop.key)}
                className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  fixed
                    ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800"
                    : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                }`}
                title={fixed ? "Fixed appointment — route is planned around this time" : "Flexible — system picks the most efficient slot"}
                data-testid={`stop-toggle-${stop.key}`}>
          {fixed ? `Fixed · ${stop.scheduled_time}` : "Flexible"}
        </button>
      ) : (
        <span className="shrink-0 text-[11px] text-slate-400 px-2.5 py-1 rounded-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          Flexible
        </span>
      )}
    </div>
  );
}

function PlanStopCard({ stop }) {
  return (
    <div className="relative pl-10" data-testid={`plan-stop-${stop.key}`}>
      <div className="absolute left-0 top-0 h-full flex flex-col items-center">
        <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center z-10">
          {stop.order}
        </div>
        <div className="flex-1 w-px bg-slate-200 dark:bg-slate-700" />
      </div>
      <p className="text-[11px] text-slate-500 mb-1 inline-flex items-center gap-1">
        <CarSimple className="h-3.5 w-3.5" /> {stop.leg_distance_miles} mi · {stop.leg_duration_minutes} min drive
      </p>
      <div className="bg-card border border-border rounded-lg p-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100">ETA {stop.eta}</span>
          {stop.fixed && stop.scheduled_time && (
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Booked {stop.scheduled_time}
            </span>
          )}
          {stop.wait_minutes > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
              wait {stop.wait_minutes}m
            </span>
          )}
          {stop.late_minutes > 0 && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">
              ~{stop.late_minutes}m late
            </span>
          )}
          <TypeBadge type={stop.type} />
        </div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mt-1">{stop.title}</p>
        <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
          <MapPin className="h-3 w-3" /> {stop.address}
        </p>
        <p className="text-[11px] text-slate-400 mt-1">On site {stop.start_work} → leave {stop.depart}</p>
      </div>
    </div>
  );
}

export default function RoutePlannerPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [home, setHome] = useState(null);
  const [stops, setStops] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [dayStart, setDayStart] = useState("08:00");
  const [duration, setDuration] = useState(60);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(false);
  const [plan, setPlan] = useState(null);

  const loadDay = useCallback(async (d) => {
    setLoading(true);
    setPlan(null);
    setOverrides({});
    try {
      const { data } = await API.get(`/in-house-portal/route-plan/day-jobs?date=${d}`);
      setStops(data.stops || []);
      setSkipped(data.skipped || []);
      setHome(data.home_location || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load the day's jobs");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadDay(date); }, [date, loadDay]);

  const isFixed = (s) => (s.key in overrides ? overrides[s.key] : s.fixed);
  const toggleFixed = (key) => {
    const s = stops.find((x) => x.key === key);
    if (!s) return;
    setOverrides((o) => ({ ...o, [key]: !(key in o ? o[key] : s.fixed) }));
    setPlan(null);
  };

  const buildPlan = async () => {
    if (!home) { toast.error("Set your home location first"); return; }
    setPlanning(true);
    try {
      const { data } = await API.post("/in-house-portal/route-plan", {
        date, overrides, day_start: dayStart, default_duration_minutes: duration,
      });
      if (!data.stops?.length) {
        toast.info(data.message || "No routable jobs for this day");
        setPlan(null);
      } else {
        setPlan(data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not plan the route");
    } finally { setPlanning(false); }
  };

  const fmtDrive = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`);

  return (
    <div className="p-6 space-y-5 max-w-3xl mx-auto" data-testid="route-planner-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <MapTrifold className="h-7 w-7 text-blue-600" weight="duotone" /> Plan My Day
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          The most efficient job order from home, round every visit, and back — with one-tap navigation.
        </p>
      </div>

      <HomeLocationCard home={home} onSaved={setHome} />

      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Day</Label>
            <DateInput value={date} onChange={(e) => setDate(e.target.value)} data-testid="route-date-input" />
          </div>
          <div>
            <Label className="text-xs">Leave home at</Label>
            <Input type="time" value={dayStart} onChange={(e) => { setDayStart(e.target.value); setPlan(null); }} data-testid="route-day-start" />
          </div>
          <div>
            <Label className="text-xs">Time per job (min)</Label>
            <Input type="number" min={5} max={480} value={duration}
                   onChange={(e) => { setDuration(parseInt(e.target.value) || 60); setPlan(null); }}
                   data-testid="route-duration-input" />
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-4 text-center">Loading jobs…</p>
        ) : stops.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center" data-testid="no-jobs-message">
            No jobs booked for this day.
          </p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/40 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {stops.length} job{stops.length === 1 ? "" : "s"} booked — tap a time chip to switch fixed / flexible
            </div>
            {stops.map((s) => (
              <StopToggleRow key={s.key} stop={s} fixed={isFixed(s)} onToggle={toggleFixed} />
            ))}
          </div>
        )}

        {skipped.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-800 dark:text-amber-200" data-testid="skipped-jobs-warning">
            <p className="font-semibold inline-flex items-center gap-1"><Warning className="h-3.5 w-3.5" /> Not routable:</p>
            {skipped.map((s, i) => <p key={i} className="mt-1">• {s.title} — {s.reason}</p>)}
          </div>
        )}

        <Button className="w-full h-11 text-base bg-blue-600 hover:bg-blue-700"
                onClick={buildPlan} disabled={planning || loading || !home || stops.length === 0}
                data-testid="plan-route-btn">
          <Path className="h-5 w-5 mr-2" weight="bold" />
          {planning ? "Planning your route…" : "Plan my route"}
        </Button>
      </div>

      {plan && (
        <div className="space-y-4" data-testid="route-plan-result">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{plan.totals.distance_miles} mi</p>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Total mileage</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{fmtDrive(plan.totals.drive_minutes)}</p>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Driving</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-slate-900 dark:text-slate-100">{plan.totals.stops}</p>
              <p className="text-[11px] text-slate-500 uppercase font-semibold">Stops</p>
            </div>
          </div>

          {plan.warnings?.length > 0 && (
            <div className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 p-3 text-xs text-rose-800 dark:text-rose-200" data-testid="plan-warnings">
              {plan.warnings.map((w, i) => <p key={i} className="inline-flex items-start gap-1"><Warning className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}</p>)}
            </div>
          )}

          <Button className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => window.open(plan.google_maps_url, "_blank", "noopener")}
                  data-testid="open-gmaps-btn">
            <NavigationArrow className="h-5 w-5 mr-2" weight="fill" /> Open route in Google Maps
          </Button>
          {plan.waypoints_truncated && (
            <p className="text-[11px] text-amber-600 text-center">
              Google Maps navigation supports 9 stops max — the first 9 are included. The full order is listed below.
            </p>
          )}

          <div className="pt-1">
            <div className="relative pl-10 pb-3">
              <div className="absolute left-0 top-0 h-full flex flex-col items-center">
                <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center z-10">
                  <House className="h-3.5 w-3.5" weight="fill" />
                </div>
                <div className="flex-1 w-px bg-slate-200 dark:bg-slate-700" />
              </div>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 pt-1">Leave home — {plan.depart_home}</p>
              <p className="text-xs text-slate-500 truncate">{plan.home.address}</p>
            </div>
            {plan.stops.map((s) => <PlanStopCard key={s.key} stop={s} />)}
            <div className="relative pl-10">
              <div className="absolute left-0 top-0">
                <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                  <House className="h-3.5 w-3.5" weight="fill" />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mb-1 inline-flex items-center gap-1">
                <CarSimple className="h-3.5 w-3.5" /> {plan.return_leg.distance_miles} mi · {plan.return_leg.duration_minutes} min drive
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Back home — {plan.arrive_home}</p>
            </div>
          </div>

          {plan.estimated && (
            <p className="text-[11px] text-slate-400 text-center">
              Drive times are straight-line estimates (live road data unavailable right now).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
